import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Switch, Col, Row, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import CustomSelect from '../../../../../../components/CustomSelect';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    oneClient,
    createClientDataStore,
    clientDataStore,
    clientDataStoreLoadingStatus,
    getClientDataStore,
    updateClient,
    deleteClientLogoByCountryCode,
    deleteClientFaviconByCountryCode,
} from '../../../../../../redux/slices/clientsSlice';
import { toast } from 'react-toastify';
import LogosUploaderCrm from '../../../../../../components/LogosUploader/LogosUploaderCrm';
import CustomMultiselect from '../../../../../../components/CustomMultiselect';
import {
    accountTypesOptions,
    countriesOptions,
    languagesOptions as languages,
} from '../../../../../../shared/data/clientDetailsOptions';
import { createShallowCopy } from '../../../../../../utils/helpFunctions';

const languagesOptions = languages.filter((el) => el.id !== 'IS');

const REGEXP_ACCEPT_IMAGE = /(image\/svg\+xml|image\/png|image\/jpe?g)/i;

export default function CrmGeneralPage() {
    const params = useParams();

    const dispatch = useDispatch();
    const client = useSelector(oneClient);
    const dataStore = useSelector(clientDataStore);
    const dataStoreLoading = useSelector(clientDataStoreLoadingStatus);

    const uploadBigLogoButtonRef = useRef(null);
    const uploadFaviconLogoButtonRef = useRef(null);

    const [selectedEmployeeValues, setSelectedEmployeeValues] = useState([]);
    const [data, setData] = useState({
        show_ssn_in_crm: true,
        is_crm_2fa_enabled: false,
        is_crm_webhooks_enabled: false,
        show_alarms_in_crm: false,
        account_languages: [],
        account_countries: [],
        account_types: [],
        logos_sizes: {
            web_app: {
                logo: {
                    se: { width: '', height: '' },
                    no: { width: '', height: '' },
                    fi: { width: '', height: '' },
                    dk: { width: '', height: '' },
                    is: { width: '', height: '' },
                },
                // mark: {...},
                // email: {...},
                // favicon: {...},
            },
            crm: {
                logo: {
                    width: '',
                    height: '',
                },
                // mark: {...},
                // email: {...},
                // favicon: {...},
            },
        },
    });
    const [bigLogo, setBigLogo] = useState({ logoUrl: '', logoData: '' });
    const [faviconLogo, setFaviconLogo] = useState({
        logoUrl: '',
        logoData: '',
    });
    const [bigLogoErrors, setBigLogoErrors] = useState({
        isWrongFileType: false,
        isFileOversized: false,
    });
    const [faviconLogoErrors, setFaviconLogoErrors] = useState({
        isWrongFileType: false,
        isFileOversized: false,
    });
    const [isAllowedSubmit, setIsAllowedSubmit] = useState(false);
    const faviconUrl = dataStore?.favicon_url_crm;
    const logoUrl = dataStore?.logo_url_crm;

    const [arrow] = useState('Show');

    const mergedArrow = useMemo(() => {
        if (arrow === 'Hide') {
            return false;
        }

        if (arrow === 'Show') {
            return true;
        }

        return {
            pointAtCenter: true,
        };
    }, [arrow]);

    const createCountriesLocalizationsData = (data) => {
        return data?.reduce((acc, el) => {
            acc[el.country_code] = el;
            return acc;
        }, {});
    };

    const localizations = createCountriesLocalizationsData(
        client?.countries_localizations,
    );

    useEffect(() => {
        if (!client.client_uid) return;

        const employeeValues = dataStore.max_employee_values?.map((el) => {
            return { value: el, label: el };
        });

        setSelectedEmployeeValues(employeeValues);
    }, [client, dataStore]);

    useEffect(() => {
        setData({
            show_ssn_in_crm:
                dataStore.show_ssn_in_crm === undefined
                    ? true
                    : dataStore.show_ssn_in_crm,
            is_crm_2fa_enabled:
                dataStore.is_crm_2fa_enabled === undefined
                    ? false
                    : dataStore.is_crm_2fa_enabled,
            is_crm_webhooks_enabled:
                dataStore.is_crm_webhooks_enabled === undefined
                    ? false
                    : dataStore.is_crm_webhooks_enabled,
            show_alarms_in_crm:
                dataStore.show_alarms_in_crm === undefined
                    ? false
                    : dataStore.show_alarms_in_crm,
            account_languages:
                dataStore?.account_languages ||
                languagesOptions.map((el) => el.id),
            account_countries:
                dataStore?.account_countries ||
                countriesOptions.map((el) => el.id),
            account_types: dataStore?.account_types ?? [],
            logos_sizes: dataStore?.logos_sizes ?? {
                web_app: {
                    logo: {
                        se: { width: '', height: '' },
                        no: { width: '', height: '' },
                        fi: { width: '', height: '' },
                        dk: { width: '', height: '' },
                        is: { width: '', height: '' },
                    },
                },
                crm: {
                    logo: {
                        width: '',
                        height: '',
                    },
                },
            },
        });

        setBigLogo((prev) => ({ ...prev, logoUrl: dataStore?.logo_url_crm }));
        setFaviconLogo((prev) => ({
            ...prev,
            logoUrl: dataStore?.favicon_url_crm,
        }));
    }, [client, dataStore]);

    const onFileSelect = (e) => {
        const loaderName = e.target.name;

        if (e.target.files && e.target.files[0]) {
            if (!REGEXP_ACCEPT_IMAGE.test(e.target.files[0].type)) {
                if (loaderName === 'big-logo') {
                    setBigLogoErrors({
                        ...bigLogoErrors,
                        isWrongFileType: true,
                    });
                }
                if (loaderName === 'favicon-logo') {
                    setFaviconLogoErrors({
                        ...bigLogoErrors,
                        isWrongFileType: true,
                    });
                }
                return;
            }

            if (e.target.files[0].size > 2097152) {
                if (loaderName === 'big-logo') {
                    setBigLogoErrors({
                        ...bigLogoErrors,
                        isFileOversized: true,
                    });
                }
                if (loaderName === 'favicon-logo') {
                    setFaviconLogoErrors({
                        ...bigLogoErrors,
                        isFileOversized: true,
                    });
                }
                return;
            }

            setBigLogoErrors({
                isFileOversized: false,
                isWrongFileType: false,
            });
            setFaviconLogoErrors({
                isFileOversized: false,
                isWrongFileType: false,
            });

            const bigLogoUrl = window.URL.createObjectURL(e.target.files[0]);
            const faviconLogoUrl = window.URL.createObjectURL(
                e.target.files[0],
            );

            if (loaderName === 'big-logo') {
                setBigLogo({
                    logoUrl: bigLogoUrl,
                    logoData: e.target.files[0],
                });
            }
            if (loaderName === 'favicon-logo') {
                setFaviconLogo({
                    logoUrl: faviconLogoUrl,
                    logoData: e.target.files[0],
                });
            }

            setIsAllowedSubmit(true);
        }

        uploadBigLogoButtonRef.current.value = '';
        uploadFaviconLogoButtonRef.current.value = '';
    };

    const onDeleteBigLogo = async (e) => {
        e.preventDefault();
        if (logoUrl) {
            await dispatch(
                deleteClientLogoByCountryCode({
                    id: client?.id,
                    countryCode: 'crm',
                }),
            ).unwrap();
            toast.success('Logo successfully deleted');
        }

        setBigLogo({ logoData: '', logoUrl: '' });
    };

    const onDeleteFaviconLogo = async (e) => {
        e.preventDefault();
        if (faviconUrl) {
            await dispatch(
                deleteClientFaviconByCountryCode({
                    id: client?.id,
                    countryCode: 'crm',
                }),
            ).unwrap();
            toast.success('Favicon successfully deleted');
        }
        setFaviconLogo({ logoData: '', logoUrl: '' });
    };

    const handleNumberOnlyInput = (e) => {
        if (
            !/[0-9]/i.test(e.key) &&
            e.nativeEvent.code !== 'Enter' &&
            e.nativeEvent.code !== 'Backspace'
        ) {
            e.preventDefault();
        }
    };

    const onSwitchChange = (checked, fieldName) => {
        setIsAllowedSubmit(true);

        setData({
            ...data,
            [fieldName]: checked,
        });
    };

    const onSelectChange = (values, name) => {
        setIsAllowedSubmit(true);

        if (name === 'employee') setSelectedEmployeeValues(values);
    };

    const onSubmit = async () => {
        try {
            let fields = [
                {
                    key_name: 'show_ssn_in_crm',
                    value: data.show_ssn_in_crm,
                },
                {
                    key_name: 'is_crm_2fa_enabled',
                    value: data.is_crm_2fa_enabled,
                },
                {
                    key_name: 'is_crm_webhooks_enabled',
                    value: data.is_crm_webhooks_enabled,
                },
                {
                    key_name: 'show_alarms_in_crm',
                    value: data.show_alarms_in_crm,
                },
                {
                    key_name: 'account_languages',
                    value: data.account_languages,
                },
                {
                    key_name: 'account_countries',
                    value: data.account_countries,
                },
                {
                    key_name: 'account_types',
                    value: data.account_types,
                },
                {
                    key_name: 'logos_sizes',
                    value: data.logos_sizes,
                },
            ];

            await dispatch(
                updateClient({
                    body: getClientFormData(client),
                    id: params.id,
                }),
            ).unwrap();

            if (selectedEmployeeValues?.length)
                fields.push({
                    key_name: 'max_employee_values',
                    value: selectedEmployeeValues.map((el) => el.value),
                });

            await dispatch(
                createClientDataStore({
                    id: client.client_uid,
                    body: { fields },
                    collectionName: 'clients',
                }),
            ).unwrap();
            await dispatch(
                getClientDataStore({
                    id: client.client_uid,
                    collectionName: 'clients',
                }),
            ).unwrap();

            toast.success('Changes saved successfully');
        } catch (error) {
            console.log(error);
        } finally {
            setIsAllowedSubmit(false);
        }
    };

    const getClientFormData = (data) => {
        const fd = new FormData();

        const formDataObject = {};

        fd.append('logo_crm', bigLogo.logoData);
        fd.append('favicon_crm', faviconLogo.logoData);

        fd.append('theme_id', client?.theme?.id);

        Object.entries(data).forEach(([key, value]) => {
            if (key === 'countries_localizations') {
                fd.append(key, JSON.stringify(localizations));
            } else {
                fd.append(key, value);
            }
        });

        fd.forEach((value, key) => {
            formDataObject[key] = value;
        });
        return formDataObject;
    };

    const onMultiselectChange = useCallback(
        (options, selectName) => {
            setIsAllowedSubmit(true);
            const opts = options.map((el) => el.value);

            setData({
                ...data,
                [selectName]: opts,
            });
        },
        [data],
    );

    const createSelectOptions = (value, options) =>
        value?.reduce((acc, el) => {
            for (const option of options) {
                if (option.id.includes(el)) acc.push(option);
            }
            return acc;
        }, []);

    const onLogosSizesChange = (e) => {
        const [logoType, parameter] = e.target.id.split('_');
        const value = e.target.value;
        const dataCopy = createShallowCopy(data);

        dataCopy.logos_sizes.crm[logoType][parameter] = value;

        setData(dataCopy);
        setIsAllowedSubmit(true);
    };

    return (
        <>
            <Helmet>
                <title>{`Console Defentry - Clients - ${client.name} - CRM - Settings`}</title>
            </Helmet>
            <Row>
                <Col span={10}>
                    <form>
                        <div className="form__item-wrapper">
                            <p className="input-label">Logos</p>
                            <LogosUploaderCrm
                                onFileSelect={onFileSelect}
                                bigLogo={bigLogo}
                                faviconLogo={faviconLogo}
                                bigLogoErrors={bigLogoErrors}
                                faviconLogoErrors={faviconLogoErrors}
                                onDeleteBigLogo={onDeleteBigLogo}
                                onDeleteFaviconLogo={onDeleteFaviconLogo}
                                onLogosSizesChange={onLogosSizesChange}
                                bigLogoButtonRef={uploadBigLogoButtonRef}
                                faviconLogoButtonRef={
                                    uploadFaviconLogoButtonRef
                                }
                                logosSizes={data?.logos_sizes}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <CustomSelect
                                labelText="Max users count values"
                                placeholder="Enter values..."
                                name="max_employee_values"
                                options={[]}
                                value={selectedEmployeeValues}
                                isLoading={dataStoreLoading === 'loading'}
                                onChange={(values) =>
                                    onSelectChange(values, 'employee')
                                }
                                onKeyDown={handleNumberOnlyInput}
                                isCreatable
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Show SSN in CRM</p>
                            <div>
                                <Switch
                                    onChange={(e) =>
                                        onSwitchChange(e, 'show_ssn_in_crm')
                                    }
                                    checked={data?.show_ssn_in_crm}
                                    className="details-table__switch--general"
                                >
                                    Yes
                                </Switch>
                            </div>
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Enabled 2FA in CRM</p>
                            <div>
                                <Switch
                                    onChange={(e) =>
                                        onSwitchChange(e, 'is_crm_2fa_enabled')
                                    }
                                    checked={data?.is_crm_2fa_enabled}
                                    className="details-table__switch--general"
                                >
                                    Yes
                                </Switch>
                            </div>
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Enabled Webhooks in CRM</p>
                            <div>
                                <Switch
                                    onChange={(e) =>
                                        onSwitchChange(e, 'is_crm_webhooks_enabled')
                                    }
                                    checked={data?.is_crm_webhooks_enabled}
                                    className="details-table__switch--general"
                                >
                                    Yes
                                </Switch>
                            </div>
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Show Alarms in CRM</p>
                            <div>
                                <Switch
                                    onChange={(e) =>
                                        onSwitchChange(e, 'show_alarms_in_crm')
                                    }
                                    checked={data?.show_alarms_in_crm}
                                    className="details-table__switch--general"
                                >
                                    Yes
                                </Switch>
                            </div>
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">
                                Languages{' '}
                                <Tooltip
                                    placement="top"
                                    title="First language in the list is set as default"
                                    arrow={mergedArrow}
                                    overlayClassName="ant-tooltip-custom"
                                >
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </p>
                            <CustomMultiselect
                                onChange={(option) =>
                                    onMultiselectChange(
                                        option,
                                        'account_languages',
                                    )
                                }
                                options={languagesOptions}
                                placeholder="Select languages"
                                value={createSelectOptions(
                                    data?.account_languages,
                                    languagesOptions,
                                )}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                                showAllTab={false}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">
                                Country{' '}
                                <Tooltip
                                    placement="top"
                                    title="First country in the list is set as default"
                                    arrow={mergedArrow}
                                    overlayClassName="ant-tooltip-custom"
                                >
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </p>

                            <CustomMultiselect
                                onChange={(option) =>
                                    onMultiselectChange(
                                        option,
                                        'account_countries',
                                    )
                                }
                                options={countriesOptions}
                                placeholder="Select countries"
                                value={createSelectOptions(
                                    data?.account_countries,
                                    countriesOptions,
                                )}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                                showAllTab={false}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Account types</p>
                            <CustomMultiselect
                                onChange={(option) =>
                                    onMultiselectChange(option, 'account_types')
                                }
                                options={accountTypesOptions}
                                placeholder="Select account types"
                                value={createSelectOptions(
                                    data?.account_types,
                                    accountTypesOptions,
                                )}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                                showAllTab={false}
                                // errorText={errors?.account_types}
                            />
                        </div>
                        <div className="form__footer">
                            <button
                                className="modal__button modal__button-accept"
                                type="button"
                                onClick={onSubmit}
                                disabled={!isAllowedSubmit}
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </Col>
            </Row>
        </>
    );
}
